import React from "react"
import { connectInfiniteHits } from "react-instantsearch-dom"
import { Helmet } from "react-helmet"

export const getHitImageUrl = hit => {
  let url = ""
  if (hit?.images && hit.images.length > 0) {
    const img = hit.images[0]
    if (typeof img === "string") url = img
    else if (img?.url) url = img.url
    else if (img?.["1600x800"]) url = img["1600x800"]
    else if (img?.["730x492"]) url = img["730x492"]
    else if (img?.["425x300"]) url = img["425x300"]
    else if (img?.["326x218"]) url = img["326x218"]
    else if (typeof img === "object") {
      const vals = Object.values(img)
      const found = vals.find(
        v =>
          typeof v === "string" &&
          (v.startsWith("http://") || v.startsWith("https://"))
      )
      if (found) url = found
    }
  }
  if (!url && hit?.images1 && hit.images1.length > 0) {
    const img1 = hit.images1[0]
    if (typeof img1 === "string") url = img1
    else if (img1?.url) url = img1.url
    else if (img1?.["730x492"]) url = img1["730x492"]
    else if (img1?.["425x300"]) url = img1["425x300"]
    else if (img1?.["326x218"]) url = img1["326x218"]
  }
  if (url && url.startsWith("/")) {
    url = `https://www.bentleyhurst.co.uk${url}`
  }
  return url
}

const HaleListingSchema = ({ hits, location, type = "sales" }) => {
  const pathname =
    location?.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "")
  const isHale =
    pathname.includes("/in-hale") ||
    pathname.endsWith("/in-hale") ||
    pathname.includes("hale")

  if (!isHale) {
    return null
  }

  const isSales = type === "sales"
  const liveListings = hits || []

  const itemListElement = liveListings.map((hit, index) => {
    const uriStr = isSales ? "property-for-sale" : "property-to-rent"
    const slug = hit.slug || ""
    const id = hit.objectID || hit.id || ""
    const propertyUrl = `https://www.bentleyhurst.co.uk/${uriStr}/${slug}-${id}/`
    const imageUrl = getHitImageUrl(hit)

    const item = {
      "@type": "RealEstateListing",
      name: hit.title || hit.display_address || "Property in Hale",
      url: propertyUrl,
      ...(imageUrl ? { image: imageUrl } : {}),
      ...(hit.display_address
        ? {
            address: {
              "@type": "PostalAddress",
              streetAddress: hit.display_address,
              addressLocality: "Hale",
              addressCountry: "GB",
            },
          }
        : {}),
      ...(hit.bedroom != null && hit.bedroom !== ""
        ? { numberOfBedrooms: Number(hit.bedroom) }
        : {}),
      offers: {
        "@type": "Offer",
        priceCurrency: "GBP",
        ...(hit.price != null ? { price: String(hit.price) } : {}),
        ...(!isSales ? { unitText: "MONTH" } : {}),
        availability: "https://schema.org/InStock",
        businessFunction: isSales
          ? "http://purl.org/goodrelations/v1#Sell"
          : "http://purl.org/goodrelations/v1#LeaseOut",
      },
    }

    return {
      "@type": "ListItem",
      position: index + 1,
      item: item,
    }
  })

  const schema = {
    "@context": "https://schema.org/",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": isSales
          ? "https://www.bentleyhurst.co.uk/property/for-sale/in-hale/#webpage"
          : "https://www.bentleyhurst.co.uk/property/to-rent/in-hale/#webpage",
        url: isSales
          ? "https://www.bentleyhurst.co.uk/property/for-sale/in-hale/"
          : "https://www.bentleyhurst.co.uk/property/to-rent/in-hale/",
        name: isSales
          ? "Houses and Properties for Sale in Hale | Bentley Hurst"
          : "Properties and Apartments to Rent in Hale | Bentley Hurst",
        description: isSales
          ? "Find the best residential properties for sale in Hale with Bentley Hurst. Contact our local property specialists for help finding your next home in Hale."
          : "Browse residential properties and luxury apartments to rent in Hale and surrounding Cheshire areas with Bentley Hurst.",
        isPartOf: {
          "@type": "WebSite",
          "@id": "https://www.bentleyhurst.co.uk/#website",
          name: "Bentley Hurst",
          url: "https://www.bentleyhurst.co.uk/",
        },
        about: {
          "@type": "RealEstateAgent",
          "@id":
            "https://www.bentleyhurst.co.uk/branches/hale/#realestateagent",
          name: "Bentley Hurst Hale Branch",
          telephone: "+44-161-928-2020",
          address: {
            "@type": "PostalAddress",
            streetAddress: "162-164 Ashley Road",
            addressLocality: "Hale",
            postalCode: "WA15 9SF",
            addressCountry: "GB",
          },
        },
        mainEntity: {
          "@type": "ItemList",
          "@id": isSales
            ? "https://www.bentleyhurst.co.uk/property/for-sale/in-hale/#itemlist"
            : "https://www.bentleyhurst.co.uk/property/to-rent/in-hale/#itemlist",
          name: isSales
            ? "Properties for Sale in Hale"
            : "Properties to Rent in Hale",
          description: isSales
            ? "Listings of houses, apartments, and townhouses for sale in Hale and surrounding WA15 areas."
            : "Selection of apartments and homes available for rent in Hale, WA15.",
          itemListOrder: "https://schema.org/ItemListUnordered",
          numberOfItems: liveListings.length,
          itemListElement: itemListElement,
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": isSales
          ? "https://www.bentleyhurst.co.uk/property/for-sale/in-hale/#breadcrumb"
          : "https://www.bentleyhurst.co.uk/property/to-rent/in-hale/#breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.bentleyhurst.co.uk/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: isSales ? "Properties for Sale" : "Properties to Rent",
            item: isSales
              ? "https://www.bentleyhurst.co.uk/property/for-sale/"
              : "https://www.bentleyhurst.co.uk/property/to-rent/",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Hale",
            item: isSales
              ? "https://www.bentleyhurst.co.uk/property/for-sale/in-hale/"
              : "https://www.bentleyhurst.co.uk/property/to-rent/in-hale/",
          },
        ],
      },
    ],
  }

  return (
    <Helmet>
      <script type="application/ld+json" key="hale-listing-schema">
        {JSON.stringify(schema, null, 2)}
      </script>
    </Helmet>
  )
}

export default connectInfiniteHits(HaleListingSchema)
